import React, { useState } from "react";
import { CancelOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import WorkflowCancelStage from "./WorkflowCancelStage";
import WorkflowCaseActionBanner, { WorkflowCaseActionBannerKind } from "./WorkflowCaseActionBanner";
import { WorkflowCancelConfig } from "./workflowBuilderData";
import { WorkflowLiveCase } from "./workflowLiveCase";
import { PIF_LATER_STAGE_KEYS } from "./pifWorkflowKeys";
import useWorkflowCaseAction from "../../hooks/useWorkflowCaseAction";
import { WorkflowRunDecline } from "../../hooks/useWorkflowRunInstance";
import { useToaster } from "../../hooks/useToaster";
import { cancelWorkflowRun } from "../../services/workflowService";
import { SEVERITIES } from "../../data/constants";

const CLOSE_TO_FINISH = " Close this case to finish.";

/** "Employee not created" - the copy agreed on 80430. The owner (or an admin) also reads CLOSE_TO_FINISH. */
export const PIF_EMPLOYEE_NOT_CREATED_MESSAGE =
  "All three directors approved this PIF, but the employee couldn't be created in Enterprise automatically. " +
  "In Phase A, the employee is created through PostIF.";

/** The end screen of a PIF closed from "Employee not created": approved, not a rejection. */
export const PIF_EMPLOYEE_NOT_CREATED_END = { title: "Case closed", description: PIF_EMPLOYEE_NOT_CREATED_MESSAGE };

/** "PIF not approved": who rejected or cancelled it, and why - or the do-not-rehire lookup. */
export const toPifNotApprovedMessage = (decline: WorkflowRunDecline | null): string => {
  if (!decline) return "The lookup matched a do-not-rehire record, so this PIF can't go ahead.";
  const verb = decline.decision === "reject" ? "rejected" : "cancelled";
  const who = decline.by ? ` by ${decline.by}` : "";
  const where = decline.stageName ? ` at ${decline.stageName}` : "";
  const why = decline.reason ? ` Reason: "${decline.reason}".` : "";
  return `This PIF was ${verb}${who}${where}.${why}`;
};

export const toWaitingForOwnerMessage = (ownerName: string | null): string =>
  `Waiting for ${ownerName || "the case owner"} to close this case.`;

interface WorkflowCancelLiveProps {
  config: WorkflowCancelConfig;
  liveCase: WorkflowLiveCase;
  /** The stage's key - the two PIF Cancel stages get their own explanation and a one-click close. */
  stageKey?: string;
  isRealPif?: boolean;
  /** The caller may close the case (owner or admin); everyone else sees who it is waiting for. */
  canClose: boolean;
  ownerName: string | null;
  decline: WorkflowRunDecline | null;
  /** Told the reason once the case is cancelled, for the terminal screen. */
  onCancelled: (reason: string) => void;
}

/**
 * A Cancel stage on a real case. The engine never runs a Cancel stage by itself: the case waits here,
 * still active, until its owner (or an admin) closes it with instance/cancel - which always needs a
 * reason. On the PIF's two Cancel stages the reason is pre-filled, so closing is one click (80430).
 */
const WorkflowCancelLive: React.FC<WorkflowCancelLiveProps> = ({
  config,
  liveCase,
  stageKey,
  isRealPif = false,
  canClose,
  ownerName,
  decline,
  onCancelled,
}) => {
  const showToaster = useToaster();
  const { busy, run } = useWorkflowCaseAction();
  const [banner, setBanner] = useState<WorkflowCaseActionBannerKind | null>(null);
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null);

  // The PIF's two Cancel stages explain themselves and close in one click; any other is generic.
  const pifFacts = !isRealPif
    ? null
    : stageKey === PIF_LATER_STAGE_KEYS.employeeNotCreated
      ? PIF_EMPLOYEE_NOT_CREATED_MESSAGE
      : stageKey === PIF_LATER_STAGE_KEYS.notApproved
        ? toPifNotApprovedMessage(decline)
        : null;

  const reload = async () => {
    setBanner((await liveCase.onChanged()) ? null : "reload-failed");
  };

  const handleCancel = async (reason: string) => {
    const outcome = await run(cancelWorkflowRun, {
      instanceId: liveCase.instanceId,
      reason,
      rowVersionTag: liveCase.rowVersionTag,
    });
    if (!outcome) return;
    if (outcome.kind !== "done") {
      setInvalidMessage(outcome.kind === "invalid" ? outcome.message : null);
      setBanner(outcome.kind);
      return;
    }
    showToaster(pifFacts === null ? "Case cancelled" : "Case closed", SEVERITIES.SUCCESS);
    onCancelled(reason);
    await reload();
  };

  const actionBanner = (
    <WorkflowCaseActionBanner
      kind={banner}
      forbiddenMessage="You can't close this case."
      invalidMessage={invalidMessage}
      onReload={() => void reload()}
    />
  );

  // A generic Cancel canvas keeps its reason form - but only for someone the cancel will accept.
  if (pifFacts === null && canClose) {
    return (
      <WorkflowCancelStage
        // The engine refuses a cancel without a reason, whatever the canvas config says.
        config={{ ...config, noteRequired: true }}
        busy={busy}
        onCancel={(reason) => void handleCancel(reason)}
        banner={actionBanner}
      />
    );
  }

  // The owner reads the facts and "Close this case to finish."; everyone else, the facts and who the
  // case is waiting for.
  const shownMessage = pifFacts === null ? null : `${pifFacts}${canClose ? CLOSE_TO_FINISH : ""}`;

  return (
    <>
      {shownMessage && (
        <Alert severity="info" className="workflow-run__alert--flush">
          {shownMessage}
        </Alert>
      )}

      {canClose ? (
        <>
          {actionBanner}
          <Box className="workflow-run__actions workflow-run__actions--end">
            <Button
              className="workflow-run__decision workflow-run__decision--reject"
              variant="contained"
              startIcon={<CancelOutlined />}
              onClick={() => void handleCancel(config.defaultNote.trim() || "Closed")}
              disabled={busy}
            >
              Close case
            </Button>
          </Box>
        </>
      ) : (
        <Typography className="workflow-run__stage-copy" role="status">
          {toWaitingForOwnerMessage(ownerName)}
        </Typography>
      )}
    </>
  );
};

export default WorkflowCancelLive;
